import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Trash2, Upload } from "lucide-react";
import { appConfirm } from "@/components/appConfirm";
import { adminFetchBlob, adminPortfolioDelete, adminPortfolioList, adminPortfolioReorder, adminPortfolioUpdate, adminPortfolioUpload, extractApiError } from "@/lib/api";
import { PORTFOLIO_CATEGORIES } from "@/components/supply/portfolio";

const CategorySelect = ({ value, onChange, testId }) => (
  <select className="editorial-input text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
    {PORTFOLIO_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
  </select>
);

const Thumb = ({ token, id }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url = "";
    adminFetchBlob(token, `/admin/portfolio/${id}/thumb`).then((b) => { url = URL.createObjectURL(b); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [token, id]);
  return <div className="w-full aspect-square bg-neutral-100 overflow-hidden">{src && <img src={src} alt="" className="w-full h-full object-contain" />}</div>;
};

const Toggle = ({ checked, onChange, label, testId }) => (
  <label className="flex items-center gap-2 text-xs min-h-[36px] cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} /> {label}
  </label>
);

const UploadForm = ({ token, onDone }) => {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState("graduation");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState("");
  const upload = async () => {
    let ok = 0;
    for (let i = 0; i < files.length; i += 1) {
      setBusy(`Uploading ${i + 1} of ${files.length}\u2026`);
      try { await adminPortfolioUpload(token, files[i], category, caption); ok += 1; }
      catch (e) { toast.error(`${files[i].name}: ${extractApiError(e, "Upload failed")}`); }
    }
    setBusy(""); setFiles([]); setCaption("");
    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} added (hidden until you switch them on)`);
    onDone();
  };
  return (
    <div className="p-5 border mb-8 grid md:grid-cols-4 gap-3 items-end" style={{ borderColor: "var(--ss-border)" }} data-testid="portfolio-upload-form">
      <label className="btn-outline inline-flex items-center gap-2 cursor-pointer justify-center">
        <Upload size={14} /> {files.length ? `${files.length} photo${files.length > 1 ? "s" : ""} chosen` : "Choose photos"}
        <input type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="sr-only"
               onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="portfolio-file-input" />
      </label>
      <CategorySelect value={category} onChange={setCategory} testId="portfolio-upload-category" />
      <input className="editorial-input text-sm" maxLength={140} placeholder="Caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="portfolio-upload-caption" />
      <button className="btn-primary" disabled={!files.length || !!busy} onClick={upload} data-testid="portfolio-upload-btn">{busy || "Upload"}</button>
    </div>
  );
};

const PhotoCard = ({ p, token, idx, count, onPatch, onMove, onDelete }) => {
  const [caption, setCaption] = useState(p.caption || "");
  return (
    <div className="p-3 border flex flex-col gap-2" style={{ borderColor: p.visible ? "var(--ss-rust)" : "var(--ss-border)" }} data-testid={`portfolio-admin-card-${p.id}`}>
      <Thumb token={token} id={p.id} />
      <div className="text-[0.65rem] truncate" style={{ color: "var(--ss-muted)" }}>{p.original_filename}</div>
      <CategorySelect value={p.category} onChange={(v) => onPatch({ category: v })} testId={`portfolio-category-${p.id}`} />
      <input className="editorial-input text-sm" maxLength={140} placeholder="Caption (optional)" value={caption}
             onChange={(e) => setCaption(e.target.value)} onBlur={() => caption !== (p.caption || "") && onPatch({ caption })} data-testid={`portfolio-caption-${p.id}`} />
      <Toggle checked={p.visible} onChange={(v) => onPatch({ visible: v })} label="Show on website" testId={`portfolio-visible-${p.id}`} />
      <Toggle checked={p.featured} onChange={(v) => onPatch({ featured: v })} label="Featured (shown first)" testId={`portfolio-featured-${p.id}`} />
      <Toggle checked={p.in_catalog} onChange={(v) => onPatch({ in_catalog: v })} label="Also show near related catalog section" testId={`portfolio-catalog-${p.id}`} />
      <div className="flex gap-2 mt-1">
        <button className="btn-outline px-3" disabled={idx === 0} onClick={() => onMove(-1)} aria-label="Move up" data-testid={`portfolio-up-${p.id}`}><ArrowUp size={14} /></button>
        <button className="btn-outline px-3" disabled={idx === count - 1} onClick={() => onMove(1)} aria-label="Move down" data-testid={`portfolio-down-${p.id}`}><ArrowDown size={14} /></button>
        <button className="btn-outline px-3 ml-auto" onClick={onDelete} aria-label="Remove photo" data-testid={`portfolio-delete-${p.id}`}><Trash2 size={14} /></button>
      </div>
    </div>
  );
};

export const PortfolioAdmin = ({ token }) => {
  const [items, setItems] = useState([]);
  const load = () => adminPortfolioList(token).then(setItems).catch((e) => toast.error(extractApiError(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const patch = async (id, payload) => {
    try { await adminPortfolioUpdate(token, id, payload); setItems((xs) => xs.map((x) => (x.id === id ? { ...x, ...payload } : x))); toast.success("Saved"); }
    catch (e) { toast.error(extractApiError(e)); }
  };
  const move = async (i, d) => {
    const next = [...items];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setItems(next);
    try { await adminPortfolioReorder(token, next.map((x) => x.id)); } catch (e) { toast.error(extractApiError(e)); load(); }
  };
  const remove = async (id) => {
    if (!(await appConfirm("Remove this photo from the portfolio?"))) return;
    try { await adminPortfolioDelete(token, id); setItems((xs) => xs.filter((x) => x.id !== id)); toast.success("Photo removed"); }
    catch (e) { toast.error(extractApiError(e)); }
  };
  const live = items.filter((p) => p.visible).length;
  return (
    <div className="mt-8" data-testid="portfolio-admin">
      <div className="font-label text-xs mb-2" style={{ color: "var(--ss-rust)" }}>Made by The Social Supply — portfolio</div>
      <p className="text-sm mb-6" style={{ color: "var(--ss-cream-dim)" }} data-testid="portfolio-admin-count">
        {items.length} photo{items.length === 1 ? "" : "s"} · {live} shown on the website. New uploads stay hidden until you switch on "Show on website". Photos are optimized automatically.
      </p>
      <UploadForm token={token} onDone={load} />
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-4">
        {items.map((p, i) => (
          <PhotoCard key={p.id} p={p} token={token} idx={i} count={items.length}
                     onPatch={(payload) => patch(p.id, payload)} onMove={(d) => move(i, d)} onDelete={() => remove(p.id)} />
        ))}
      </div>
    </div>
  );
};
